import { useState } from "react";
import Footer from "../../components/Footer/Footer";
import Header from "../../components/Header/Header";
import Intro from "../../sections/Intro/Intro";
import Checkbox from "./Checkbox/Checkbox";
import css from "./Resources.module.css";
import { Link } from "react-router-dom";

const NewBusinessRegistrationChecklist = () => {
  const [progress, setProgress] = useState(0);

  const handleToggle = (weight, isChecked) => {
    setProgress((prev) => prev + (isChecked ? weight * -1 : weight));
  };

  return (
    <div className="column">
      <Header />
      <Intro
        primary="Register your business yourself."
        secondary="A new business registration checklist for entrepreneurs who take compliance seriously."
        button="Let us handle it"
      />
      <div className="column" style={{ marginTop: "4rem" }}>
        <div className={css.progress_container}>
          <div className="card column g1 background_light">
            <strong>Progress: {progress}%</strong>
            <div className={css.progress_bar}>
              <div
                className={css.progress_bar_filled}
                style={{ width: `${progress}%` }}
              ></div>
            </div>
            <p>
              Questions?{" "}
              <Link className="underline" to="/contact">
                Contact us
              </Link>
              .
            </p>
          </div>
          <img
            className="crystals_light"
            src={require("../../images/crystals_light.png")}
            alt="crystals"
          />
        </div>

        <div className="column w64 center g1" style={{ padding: "4rem 0" }}>
          <h3>Preparation</h3>
          <p className="ctext p01">
            Before starting your business, prepare answers to the following
            questions:
          </p>
          <div className="columns">
            <Checkbox onClick={handleToggle} weight={5} center>
              <p>What products or services will your business offer?</p>
            </Checkbox>
            <Checkbox onClick={handleToggle} weight={5} center>
              <p>What are you naming the business?</p>
            </Checkbox>
            <Checkbox onClick={handleToggle} weight={5} center>
              <p>
                What is the physical address for your business? If you will not
                have a public office, where will the headquarters for your
                business be located?
              </p>
            </Checkbox>
            <Checkbox onClick={handleToggle} weight={5} center>
              <p>
                Who will be the owners / members of the business, and what is
                their ownership percentage?
              </p>
            </Checkbox>
            <Checkbox onClick={handleToggle} weight={5} center>
              <p>
                Which entity type will you be registering your business as?
                (e.g. S-Corp, Partnership, Sole Proprietorship, etc.)
              </p>
            </Checkbox>
            <Checkbox onClick={handleToggle} weight={5} center>
              <p>
                How many employees do you estimate to have in the current
                calendar year?
              </p>
            </Checkbox>
            <Checkbox onClick={handleToggle} weight={5} center>
              <p>
                How much do you expect to pay in gross wages in the current
                calendar year?
              </p>
            </Checkbox>
            <Checkbox onClick={handleToggle} weight={5} center>
              <p>
                How much gross revenue is your business expecting in the current
                calendar year?
              </p>
            </Checkbox>
          </div>
        </div>
        <img
          className="crystals_light crystals_flipped"
          src={require("../../images/crystals_light.png")}
          alt="crystals"
        />
        <div
          className="column background_gradient-top"
          style={{ padding: "4rem 0" }}
        >
          <div className="column w64">
            <h3>Registration</h3>
            <p className="ctext" style={{ margin: "1rem 0" }}>
              Once you've answered all of those questions, you're ready to
              register your business.
            </p>
            <Checkbox light onClick={handleToggle} weight={10}>
              <strong>Register your business with the IRS</strong>
              <p>
                This process is <span className="underline">free</span> and can
                be done online directly through{" "}
                <a
                  className="underline"
                  target="_blank"
                  rel="noopener noreferrer"
                  href="https://www.irs.gov/businesses/small-businesses-self-employed/get-an-employer-identification-number"
                >
                  the official IRS website
                </a>
                . Once complete, you will receive a Federal Employer
                Identification Number (EIN). Your Federal business registration
                should include:
              </p>
              <ul className={css.bullets}>
                <li>CP-575</li>
              </ul>
            </Checkbox>
            <Checkbox light onClick={handleToggle} weight={10}>
              <strong>Register your business with your state</strong>
              <p>
                This process should cost between{" "}
                <span className="underline">$15 – $520</span> per year and can
                be done directly through your state's official Secretary of
                State website.{" "}
                <span className="underline">
                  Be sure to save your login information
                </span>
                . Your state business registration should include:
              </p>
              <ul className={css.bullets}>
                <li>
                  Articles of Organization <span className="underline">or</span>{" "}
                  Articles of Incorporation
                </li>
                <li>State Business Number</li>
              </ul>
              <i>If applicable:</i>
              <ul className={css.bullets}>
                <li>Temporary Income Tax Withholding Number (WTH)</li>
                <li>
                  Temporary Unemployment Insurance Employer Identification
                  Number (SUI)
                </li>
                <li>Unemployment Insurance PIN (SUI)</li>
                <li>Sales and Use Tax Number (STC)</li>
                <li>
                  Any other account numbers you requested during the
                  registration process.
                </li>
              </ul>
            </Checkbox>

            <Checkbox onClick={handleToggle} weight={10}>
              <strong>Register your business with your city</strong>
              <p>
                This process should cost between{" "}
                <span className="underline">$0 – $800</span> per year and can be
                done at the city office. (Some cities have an option to register
                online.) Your business needs to be registered in every city
                where you have a public office, headquarters, or physical
                location. When registering with the city, you will need to
                provide your state registration number and copies of your CP-575
                and [ Articles of Organization{" "}
                <span className="underline">or</span> Articles of Incorporation
                ]. Your city business registration should include:
              </p>
              <ul className={css.bullets}>
                <li>City business license</li>
              </ul>
            </Checkbox>
          </div>
        </div>

        <div className="column w64">
          <h3>Tax Account</h3>
          <p className="ctext" style={{ margin: "1rem 0", maxWidth: "64rem" }}>
            A few weeks after registration, you will receive a letter or two
            from your State with any additional tax numbers or PINs needed to
            set up your online tax accounts. It varies by state, but the
            letter(s) will include the instructions you need.
          </p>

          <Checkbox onClick={handleToggle} weight={10} center>
            <strong>Set up your state online tax portal</strong>
            <p>
              This process is <span className="underline">free</span>.{" "}
              <span className="underline">
                Be sure to save your login information
              </span>
              .
            </p>
          </Checkbox>
        </div>

        <div
          className="container background_gradient-bottom"
          style={{ padding: "4rem 0" }}
        >
          <div className="column w64">
            <h3>Unemployment Insurance</h3>
            <p
              className="ctext"
              style={{ margin: "1rem 0", maxWidth: "64rem" }}
            >
              <i>If applicable,</i> you will need to create a login to your
              state's Department of Workforce Services website and link your
              unemployment account. The numbers and PINs needed to link this
              account were assigned to your business during your state business
              registration.
            </p>
            <Checkbox light onClick={handleToggle} weight={10} center>
              <strong>Set up your unemployment insurance account</strong>
              <p>
                This process is <span className="underline">free</span>.
              </p>
            </Checkbox>
          </div>
        </div>

        <div className="column background_light">
          <div className="column w64" style={{ marginBottom: "4rem" }}>
            <h3>Bank Account</h3>
            <Checkbox light onClick={handleToggle} weight={10}>
              <strong>Open a business bank account</strong>
              <p>
                This process is <span className="underline">free</span>. You
                will need:
              </p>
              <ul className={css.bullets}>
                <li>CP – 575</li>
                <li>
                  Articles of Organization <span className="underline">or</span>{" "}
                  Articles of Incorporation
                </li>
                <li>
                  A valid form of identification for each person who will be on
                  th account.
                </li>
              </ul>
            </Checkbox>
          </div>
        </div>
      </div>
      <img
        className="crystals_light"
        src={require("../../images/crystals_light.png")}
        alt="crystals"
      />

      <div className="column g1" style={{ paddingTop: "4rem" }}>
        <h2>Maintenance</h2>
        <div className="columns w64">
          <Checkbox checked>
            <strong className={css.purple}>File and pay state taxes</strong>
            <p>
              When you register your business, you choose to file your state
              taxes monthly, quarterly, or annually.
            </p>
          </Checkbox>
          <Checkbox checked>
            <strong className={css.purple}>
              File and pay unemployment insurance
            </strong>
            <p>Your payroll management company should process this for you.</p>
          </Checkbox>
          <Checkbox checked>
            <strong className={css.purple}>
              Renew your state business registration annually
            </strong>
            <p>
              This process should cost between{" "}
              <span className="underline">$15 – $520</span> per year. You will
              receive an annual renewal notice in the mail that includes
              everything you'll need to do it on your own.
            </p>
          </Checkbox>
          <Checkbox checked>
            <strong className={css.purple}>
              Renew your city business registration annually.
            </strong>
            <p>
              This process should cost between{" "}
              <span className="underline">$0 – $800</span> per year. You will
              receive an annual renewal notice in the mail that includes
              everything you'll need to do it on your own.
            </p>
          </Checkbox>
        </div>
      </div>
      <div className="column p01" style={{ marginBottom: "4rem" }}>
        <a
          className="cta"
          href="https://calendar.app.google/cgho9MaUp1oADCZLA"
          target="_blank"
          rel="noopener noreferrer"
        >
          Let us handle it
        </a>
      </div>
      <Footer />
    </div>
  );
};

export default NewBusinessRegistrationChecklist;
